import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import ProfileImage from '../components/ProfileImage';
import GlobalFooter from '../components/GlobalFooter';
// GlobalChatSystem now rendered at App level — only the dock storage key is still
// needed here, from the chat's types.ts leaf. Never the chat barrel: it pulls the
// whole engine onto the first paint (PR-S, 2026-09-22).
import { GLOBAL_CHAT_DOCK_KEY } from '../components/GlobalChatSystem/types';
import { TESTING_MODE } from '../config/agenticWorkflowConfig';
import { isJiuAuthorizedEmail } from '../config/justiceisurrightAccess';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 10px 28px rgba(198, 167, 94, 0.16); }
  50% { box-shadow: 0 16px 34px rgba(182, 146, 76, 0.22); }
  100% { box-shadow: 0 10px 28px rgba(198, 167, 94, 0.16); }
`;

// Docked chat panel total width (420px + 24px right margin + 24px gap)
const DOCKED_CHAT_PANEL_WIDTH = 552; // pixels

const PageContainer = styled.div`
  min-height: 100dvh;
  background: var(--brand-canvas);
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: clamp(3.4rem, 7vw, 4.4rem) clamp(1rem, 4vw, 2.5rem) clamp(1rem, 2vh, 1.5rem);
  position: relative;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;

  /* Subtle ambient gradients */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60vh;
    background:
      radial-gradient(ellipse 80% 50% at 20% 10%, rgba(198, 167, 94, 0.05) 0%, transparent 60%),
      radial-gradient(ellipse 60% 40% at 85% 20%, rgba(182, 146, 76, 0.04) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
`;

// Wrapper that handles content positioning when chat is docked
const DockedContentWrapper = styled.div<{ $isDocked?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  ${props => props.$isDocked && css`
    /* When docked, constrain width to available space and position on left */
    width: calc(100vw - ${DOCKED_CHAT_PANEL_WIDTH}px);
    max-width: calc(100vw - ${DOCKED_CHAT_PANEL_WIDTH}px);
    align-self: flex-start;
    padding-right: 32px;
    padding-left: 32px;
    
    @media (max-width: 900px) {
      /* On smaller screens, reset to normal */
      width: 100%;
      max-width: 100%;
      align-self: center;
      padding-right: 0;
      padding-left: 0;
    }
  `}
`;

const HomeHeader = styled.header`
  position: fixed;
  top: 2.5rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(250, 250, 248, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--brand-line);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

  @media (max-width: 900px) {
    display: none;
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.08);
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: var(--brand-gold);
    background: rgba(198, 167, 94, 0.08);
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const MobileMenuToggle = styled.button`
  position: fixed;
  top: 1.25rem;
  right: 1rem;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 20px;
  height: 14px;
  position: relative;
  span {
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-black);
    border-radius: 2px;
    transition: all 0.25s ease;

    &:nth-child(1) {
      top: ${props => (props.isOpen ? '6px' : '0')};
      transform: ${props => (props.isOpen ? 'rotate(45deg)' : 'rotate(0)')};
    }
    &:nth-child(2) {
      top: 6px;
      opacity: ${props => (props.isOpen ? 0 : 1)};
    }
    &:nth-child(3) {
      top: ${props => (props.isOpen ? '6px' : '12px')};
      transform: ${props => (props.isOpen ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 4.5rem;
  right: 1rem;
  width: 200px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  z-index: 200000; /* above prompt bars */
  transform: ${props => (props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(-8px)')};
  opacity: ${props => (props.isOpen ? 1 : 0)};
  pointer-events: ${props => (props.isOpen ? 'auto' : 'none')};
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 901px) {
    display: none;
  }
`;

const MobileMenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--brand-text);
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.15s ease;
  margin: 0 0.35rem;
  border-radius: 8px;

  svg {
    width: 17px;
    height: 17px;
    opacity: 0.65;
  }

  &:hover {
    background: rgba(198, 167, 94, 0.08);
    color: var(--brand-gold);
  }

  &:hover svg {
    opacity: 0.9;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--brand-text);
  background: transparent;
  border: none;
  text-align: left;
  width: calc(100% - 0.7rem);
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.15s ease;
  margin: 0 0.35rem;
  border-radius: 8px;

  svg {
    width: 17px;
    height: 17px;
    opacity: 0.65;
  }

  &:hover {
    background: rgba(198, 167, 94, 0.08);
    color: var(--brand-gold);
  }

  &:hover svg {
    opacity: 0.9;
  }
`;

const MobileMenuLogoutButton = styled(MobileMenuButton)`
  color: #dc3545;

  &:hover {
    background: rgba(220, 53, 69, 0.06);
    color: #c82333;
  }
`;

const Hero = styled.section`
  width: min(1180px, 100%);
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.04) 0%, rgba(198, 167, 94, 0.1) 100%);
  border-radius: 22px;
  padding: clamp(2.4rem, 4vw, 3.5rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  box-shadow: 0 16px 44px rgba(26, 26, 26, 0.1);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--brand-text);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: lowercase;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: clamp(2.1rem, 4.4vw, 3rem);
  margin: 1rem 0 0.5rem;
  color: var(--brand-text);
  text-transform: lowercase;
  line-height: 1.1;
`;

const HeroSubtitle = styled.p`
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.05rem, 2.8vw, 1.25rem);
  max-width: 760px;
  line-height: 1.6;
  margin-bottom: 1.6rem;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

const HeroCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  padding: 1.2rem 1.35rem;
  box-shadow: 0 12px 30px rgba(26, 26, 26, 0.06);
  border: 1px solid var(--brand-line);
  animation: ${float} 8s ease-in-out infinite;

  h4 {
    margin: 0 0 0.35rem;
    font-family: 'Space Grotesk', 'Satoshi', sans-serif;
    font-size: 1.05rem;
    color: var(--brand-text);
    text-transform: lowercase;
  }

  p {
    margin: 0;
    font-family: 'Inter', sans-serif;
    color: var(--brand-text-secondary);
    line-height: 1.5;
    font-size: 0.98rem;
  }
`;

const Section = styled.section`
  width: min(1180px, 100%);
  background: #ffffff;
  border-radius: 18px;
  padding: clamp(1.75rem, 3vw, 2.5rem);
  margin-top: 1.75rem;
  box-shadow: 0 10px 28px rgba(26, 26, 26, 0.06);
  border: 1px solid var(--brand-line);
  animation: ${fadeIn} 0.6s ease;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.4rem;
  color: var(--brand-text);
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: clamp(1rem, 2vw, 1.15rem);
  font-weight: 600;
  letter-spacing: -0.01em;
  background-image: linear-gradient(120deg, rgba(198, 167, 94, 0.3) 0%, rgba(182, 146, 76, 0.2) 100%);
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 0% 8px;
  transition: color 0.2s ease, background-size 0.25s ease, transform 0.2s ease;

  &:hover {
    color: var(--brand-gold-hover);
    background-size: 100% 8px;
    transform: translateY(-1px);
  }
`;

const SectionSubtitle = styled.p`
  margin: 0 0 0.75rem;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.55;
  max-width: 60ch;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.1rem;
`;

const Card = styled.div`
  border: 1px solid var(--brand-line);
  border-radius: 14px;
  padding: 1.25rem 1.35rem;
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.03), rgba(250, 250, 248, 0.5));
  box-shadow: 0 8px 20px rgba(26, 26, 26, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(26, 26, 26, 0.07);
  }

  h3 {
    margin: 0 0 0.35rem;
    font-family: 'Space Grotesk', 'Satoshi', sans-serif;
    color: var(--brand-text);
    font-size: 1.05rem;
    text-transform: lowercase;
  }

  p {
    margin: 0;
    font-family: 'Inter', sans-serif;
    color: var(--brand-text-secondary);
    line-height: 1.5;
  }
`;

const Checklist = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.75rem 1.25rem;

  li {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
    color: var(--brand-text);
    font-family: 'Inter', sans-serif;
    line-height: 1.5;
  }
`;

const CTASection = styled.div`
  width: min(1180px, 100%);
  margin: 2.5rem auto 1rem;
  padding: clamp(1.75rem, 4vw, 2.6rem);
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.03) 0%, rgba(198, 167, 94, 0.12) 100%);
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(26, 26, 26, 0.08);
  text-align: center;
  animation: ${glow} 10s ease-in-out infinite;
`;

const CTAButton = styled.button`
  margin-top: 1rem;
  padding: 0.85rem 1.35rem;
  border-radius: 12px;
  border: none;
  background: var(--brand-black);
  color: var(--brand-gold);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 12px 26px rgba(26, 26, 26, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  text-transform: lowercase;

  &:hover {
    transform: translateY(-2px);
    background: var(--brand-charcoal);
    box-shadow: 0 14px 30px rgba(26, 26, 26, 0.28);
  }
`;

const BookingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  width: 100%;
`;

const BookingCard = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1.25rem;
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.06), rgba(250, 250, 248, 0.8));
  border: 1px solid var(--brand-line);
  border-radius: 14px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(26, 26, 26, 0.08);
    border-color: rgba(198, 167, 94, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const BookingAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand-gold);
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
`;

const BookingName = styled.div`
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--brand-text);
  text-align: center;
`;

const BookingRole = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  text-align: center;
  line-height: 1.4;
`;

const BookingCTA = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.6rem 1.1rem;
  border-radius: 10px;
  background: var(--brand-black);
  color: var(--brand-gold);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.875rem;
  transition: background 0.2s ease;

  ${BookingCard}:hover & {
    background: var(--brand-charcoal);
  }
`;

const PromptBarSpacer = styled.div`
  height: clamp(8px, 2vh, 16px);
`;

const MainContent = styled.div`
  width: 100%;
  max-width: min(1100px, 100%);
  display: flex;
  flex-direction: column;
  gap: clamp(1.25rem, 2.5vw, 1.5rem);
  margin-top: clamp(1rem, 2.5vw, 1.5rem);
  position: relative;
  z-index: 1;
  box-sizing: border-box;
`;

const PageHeading = styled.h1`
  margin: 0;
  color: var(--brand-text);
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  flex: 1;
  line-height: 1.2;
  background-image: linear-gradient(120deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 0% 12px;
  transition: background-size 0.25s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-size: 100% 12px;
    color: var(--brand-gold-hover);
    transform: translateY(-1px);
  }
`;

const HeadingLine = styled.span`
  display: block;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const SectionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: clamp(0.875rem, 1.5vw, 1rem);
  width: 100%;
  box-sizing: border-box;
`;

const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: clamp(1rem, 2.5vw, 1.5rem);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.02);
  border: 1px solid var(--brand-line);
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(120deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
    opacity: 0.9;
  }

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.04);
    transform: translateY(-2px);
  }

  a {
    color: var(--brand-gold);
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.15s ease;
  }

  a:hover {
    color: var(--brand-gold-hover);
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span<{ tone: 'live' | 'coming' | 'done' | 'inprogress' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.7rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${({ tone }) =>
    tone === 'live'
      ? '#0d6939'
      : tone === 'done'
      ? '#1e40af'
      : tone === 'inprogress'
      ? '#b45309'
      : '#495057'};
  background: ${({ tone }) =>
    tone === 'live'
      ? '#dcfce7'
      : tone === 'done'
      ? '#dbeafe'
      : tone === 'inprogress'
      ? '#fef3c7'
      : '#f1f3f5'};
  transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  cursor: default;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
  }

  @media (max-width: 640px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.55rem;
    white-space: normal;
    word-break: break-word;
    max-width: 100%;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  max-width: 100%;
  overflow: hidden;
`;

const BodyLine = styled.p`
  margin: 0;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.925rem;
  line-height: 1.55;
  max-width: 60ch;
`;

const SmallMeta = styled.p`
  margin: 0;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  line-height: 1.45;
  max-width: 60ch;
`;

const SingleLineMeta = styled(SmallMeta)`
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  font-weight: 600;
  color: var(--brand-text);
  display: block;
  width: 100%;
  max-width: none;

  @media (max-width: 640px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

const TimelineList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: clamp(0.6rem, 1.5vw, 0.85rem);
  width: 100%;
  box-sizing: border-box;
`;

const TimelineItem = styled.div`
  background: var(--brand-canvas);
  border: 1px solid var(--brand-line);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.45;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  transition: background 0.15s ease, transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
  min-width: 0;
  box-sizing: border-box;

  &:hover {
    background: rgba(198, 167, 94, 0.04);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(26, 26, 26, 0.06);
  }

  @media (max-width: 640px) {
    padding: 0.75rem 0.85rem;
  }
`;

const TimelineLabel = styled.div`
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-weight: 600;
  font-size: 0.8rem;
  color: var(--brand-gold);
  letter-spacing: 0.01em;
`;

const TimelineText = styled.div`
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const ClickHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.12) 0%, rgba(182, 146, 76, 0.18) 100%);
  color: var(--brand-gold-hover);
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 12px;
  white-space: nowrap;
  transition: all 0.2s ease;
  width: fit-content;
  margin-top: auto;
  max-width: 100%;

  @media (max-width: 640px) {
    white-space: normal;
    font-size: 0.7rem;
  }

  &::after {
    content: '→';
    font-size: 0.7rem;
    transition: transform 0.2s ease;
  }
`;

const ClickableTimelineItem = styled(TimelineItem)`
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgba(198, 167, 94, 0.06);
    border-color: rgba(198, 167, 94, 0.25);

    ${ClickHint} {
      background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-charcoal) 100%);
      color: var(--brand-gold);

      &::after {
        transform: translateX(2px);
      }
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const GateWrapper = styled.div`
  width: min(400px, 92vw);
  margin: clamp(5rem, 12vw, 8rem) auto;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--brand-line);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06), 0 12px 40px rgba(0, 0, 0, 0.04);
  padding: clamp(2rem, 5vw, 2.5rem);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 20%, rgba(198, 167, 94, 0.08), transparent 40%),
      radial-gradient(circle at 80% 10%, rgba(182, 146, 76, 0.06), transparent 40%);
    z-index: 0;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08), 0 14px 44px rgba(0, 0, 0, 0.06);
  }
`;

const GateTitle = styled.h1`
  margin: 0 0 0.75rem;
  color: var(--brand-text);
  font-family: 'Space Grotesk', 'Satoshi', sans-serif;
  font-size: clamp(1.4rem, 3vw, 1.6rem);
  font-weight: 600;
  position: relative;
  display: inline-block;
  padding-bottom: 6px;
  background-image: linear-gradient(120deg, rgba(198, 167, 94, 0.3) 0%, rgba(182, 146, 76, 0.2) 100%);
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 0% 6px;
  transition: background-size 0.25s ease, transform 0.2s ease, color 0.2s ease;

  &:hover {
    background-size: 100% 6px;
    color: var(--brand-gold-hover);
    transform: translateY(-1px);
  }
`;

const GateSubtitle = styled.p`
  margin: -0.1rem 0 1rem;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.01em;
`;

const GateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.25rem;
  font-family: 'Inter', sans-serif;
`;

const GateInput = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--brand-line);
  background: var(--brand-canvas);
  color: var(--brand-text);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.15);
    outline: none;
    background: #fff;
  }

  &::placeholder {
    color: var(--brand-text-placeholder);
  }
`;

const GateButton = styled.button`
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: none;
  background: var(--brand-black);
  color: var(--brand-gold);
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: var(--brand-charcoal);
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(26, 26, 26, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GateHint = styled.div`
  margin-top: 0.35rem;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

const GateSupport = styled.div`
  margin-top: 1rem;
  color: var(--brand-text-secondary);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  line-height: 1.5;
  transition: color 0.2s ease;

  &:hover {
    color: var(--brand-gold);
  }
`;

const TopBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: clamp(0.75rem, 2vw, 1rem);
  padding: clamp(0.5rem, 1.5vw, 0.75rem) 0;
  margin-top: clamp(0.25rem, 1vw, 0.5rem);
  z-index: 20;
  position: relative;
  flex-wrap: wrap;
  row-gap: clamp(0.35rem, 1vw, 0.6rem);
  text-align: center;

  @media (max-width: 640px) {
    padding: 0.5rem 0 0.25rem;
    gap: 0.45rem;
    margin-top: 0.75rem;
  }
`;

const LogoutButton = styled.button`
  border: 1px solid var(--brand-line);
  background: var(--brand-black);
  color: var(--brand-gold);
  padding: 0.6rem 1rem;
  border-radius: 12px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: lowercase;
  box-shadow: 0 10px 22px rgba(26, 26, 26, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-1px);
    background: var(--brand-charcoal);
    box-shadow: 0 14px 26px rgba(26, 26, 26, 0.24);
    border-color: var(--brand-line);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 640px) {
    margin-left: auto;
    margin-right: auto;
    width: min(320px, 90%);
    justify-content: center;
  }
`;

const GateError = styled.div`
  color: #dc3545;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const navLinks = [
  
  {
    to: '/team',
    label: 'team',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    )
  },
  {
    to: '/mission',
    label: 'mission',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    )
  },
  {
    to: '/insights',
    label: 'insights',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="2" x2="12" y2="6" />
        <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
        <line x1="9" y1="22" x2="15" y2="22" />
      </svg>
    )
  },
  {
    to: '/',
    label: 'home',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    )
  },
];

const JusticeIsRight: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Belt-and-braces account check (Round M, 2026-07-15). The route-level
  // <JusticeAccessGate> in App.tsx is the enforcement point (it redirects
  // everyone who is not the allowlisted account); this internal check keeps
  // the page safe even if it is ever mounted without the gate. Allowlist
  // lives in ONE place: src/config/justiceisurrightAccess.ts.
  const isAuthorizedUser = isAuthenticated && isJiuAuthorizedEmail(user?.email);

  // Track chat dock state to adjust layout when docked right
  const [isChatDockedRight, setIsChatDockedRight] = useState<boolean>(() => {
    try {
      return localStorage.getItem(GLOBAL_CHAT_DOCK_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Initialize the page-layout dock state from the last-saved value.
  // Per-tab view independence (2026-08-09): the storage-event listener that
  // adopted OTHER tabs' dock changes live is gone — view state is per-tab;
  // only new-tab initialization reads the shared flag.
  useEffect(() => {
    try {
      const initial = localStorage.getItem(GLOBAL_CHAT_DOCK_KEY) === '1';
      setIsChatDockedRight(initial);
    } catch {
      /* noop */
    }
  }, []);

  const handleLogin = () => {
    navigate('/auth', { state: { returnUrl: '/justiceisurright' } });
  };

  // Helper to open chat with a trigger phrase (uses custom event for reliability)
  // This dispatches a custom event that GlobalChatSystem listens for, bypassing DOM click issues
  const openChatWithTrigger = (triggerPhrase: string) => {
    document.dispatchEvent(new CustomEvent('globalchat:open', {
      detail: { triggerPhrase }
    }));
  };

  // Helper to open the file attachment picker (uses custom event for reliability)
  const openFileAttachment = () => {
    document.dispatchEvent(new CustomEvent('globalchat:open', {
      detail: { openFile: true }
    }));
  };

  if (!isAuthorizedUser) {
    return (
      <PageContainer>
        <SeoHead
          title="Justice Is Right | Access"
          tabTitle="justiceisurright access"
          description="Secure access gate for The Law Offices of Fernando Hidalgo."
          canonicalUrl="https://prest0.ai/justiceisurright"
          schemaType="WebPage"
          noIndex
        />
        <GateWrapper>
          <GateTitle>The Law Offices of Fernando Hidalgo</GateTitle>
          <GateSubtitle>private workspace</GateSubtitle>

          <GateHint style={{ margin: '1.5rem 0' }}>
            {isAuthenticated
              ? `signed in as ${user?.email}`
              : 'sign in to access this page'}
          </GateHint>

          <GateButton type="button" onClick={handleLogin}>
            {isAuthenticated ? 'switch account' : 'sign in'}
          </GateButton>
        </GateWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DockedContentWrapper $isDocked={isChatDockedRight}>
        <SeoHead
          title="Justice Is Right | AI transformation"
          tabTitle="justiceisurright"
          description="A private AI transformation workspace for The Law Offices of Fernando Hidalgo."
          canonicalUrl="https://prest0.ai/justiceisurright"
          schemaType="WebPage"
          noIndex
        />

        <HomeHeader>
        {navLinks.map(link => (
          <HeaderIconLink key={link.to} to={link.to}>
            {link.icon}
            <span>{link.label}</span>
          </HeaderIconLink>
        ))}
        {isAuthenticated ? (
          <ProfileImage size={34} />
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            <span>login</span>
          </HeaderIconButton>
        )}
        <NotificationsBell />
      </HomeHeader>

      <MobileMenuToggle onClick={() => setMenuOpen(prev => !prev)}>
        <MobileMenuIcon isOpen={menuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>

      <MobileMenuDropdown isOpen={menuOpen} ref={mobileMenuRef}>
        {navLinks.map(link => (
          <MobileMenuLink key={link.to} to={link.to} onClick={() => setMenuOpen(false)}>
            {link.icon}
            {link.label}
          </MobileMenuLink>
        ))}
        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                wallet
              </MobileMenuLink>
            )}

            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity" onClick={() => setMenuOpen(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}

            <MobileMenuLink to="/profile" onClick={() => setMenuOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              profile
            </MobileMenuLink>
            <MobileMenuLogoutButton
              onClick={() => {
                logout();
                setMenuOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileMenuLogoutButton>
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
          </>
        ) : (
          <MobileMenuButton
            onClick={() => {
              setMenuOpen(false);
              handleLogin();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

        <MainContent style={{ marginTop: 'clamp(2rem, 5vw, 3rem)' }}>
          <TopBar>
            <PageHeading>
              <HeadingLine>The Law Offices of Fernando Hidalgo</HeadingLine>
              <HeadingLine>AI Transformation Program Center</HeadingLine>
            </PageHeading>
          <LogoutButton
            type="button"
            onClick={() => {
              logout();
              navigate('/');
            }}
            aria-label="sign out"
            title="Sign out and leave this workspace"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            sign out
          </LogoutButton>
        </TopBar>

        <SectionRow>
          <SectionCard>
            <SectionTitle>Existing Tools</SectionTitle>
            <TimelineList>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Deep research ')}>
                <TimelineLabel>Deep Research AI Agent</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "deep research"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Write a video script ')}>
                <TimelineLabel>Write a Video Script AI Agent</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "write a video script"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={openFileAttachment}>
                <TimelineLabel>Document Reader AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Click to upload a document</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Translate to spanish')}>
                <TimelineLabel>English to Spanish AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "translate to spanish"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Translate to english')}>
                <TimelineLabel>Spanish to English AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "translate to english"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Create a legal document')}>
                <TimelineLabel>Legal Document Creator AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "create a legal document"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('I need to file an asylum application')}>
                <TimelineLabel>I-589 Asylum Application AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "asylum application" or "solicitud de asilo"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
              <ClickableTimelineItem onClick={() => openChatWithTrigger('Create a demand letter')}>
                <TimelineLabel>Demand Letter AI Tool</TimelineLabel>
                <TimelineText>
                  <ClickHint>Start with "demand letter" or "carta de demanda"</ClickHint>
                </TimelineText>
              </ClickableTimelineItem>
            </TimelineList>
          </SectionCard>
        </SectionRow>

        <SectionRow>
          <SectionCard>
            <SectionTitle>Tool Status</SectionTitle>
            <BadgeRow>
              <StatusBadge tone="live">research tool · launched · testing + iterating with feedback</StatusBadge>
              <StatusBadge tone="live">video script · launched · testing + iterating with feedback</StatusBadge>
              <StatusBadge tone="live">document reader · launched · testing + iterating with feedback</StatusBadge>
              <StatusBadge tone="live">spanish translator · launched · testing + iterating with feedback</StatusBadge>
              <StatusBadge tone="live">english translator · launched · testing + iterating with feedback</StatusBadge>
              <StatusBadge tone="live">document creator · launched · demand letters, NDAs, employment + contractor agreements</StatusBadge>
              <StatusBadge tone="live">I-589 asylum application · launched · auto-fills USCIS PDF + attorney worksheet</StatusBadge>
              <StatusBadge tone="live">demand letter generator · launched · English + Spanish, legal citations</StatusBadge>
              <StatusBadge tone="live">document upload auto-fill · launched · upload intake docs to populate forms</StatusBadge>
              <StatusBadge tone="live">bilingual support · launched · full Spanish UX for all document tools</StatusBadge>
              <StatusBadge tone="inprogress">search visibility · much progress/ongoing</StatusBadge>
              <StatusBadge tone="inprogress">client chatbot · in progress (week 5)</StatusBadge>
              <StatusBadge tone="inprogress">client document and meeting purchase agent · in progress (week 5)</StatusBadge>
              <StatusBadge tone="coming">legal updates · coming (week 7)</StatusBadge>
              <StatusBadge tone="coming">courtroom simulation · optional (week 8)</StatusBadge>
              <StatusBadge tone="coming">adaptive agent · launch (weeks 9-19)</StatusBadge>
            </BadgeRow>
          </SectionCard>
        </SectionRow>

        <SectionRow>
          <SectionCard>
            <SectionTitle>Contact</SectionTitle>
            <BodyLine>The primary method of communication is via email, but you can text the group chat for convenience as well.</BodyLine>
            <BodyLine>Project manager: Miles Maggio</BodyLine>
            <BodyLine><a href="mailto:miles@prest0.ai">miles@prest0.ai</a></BodyLine>
            <BodyLine><a href="https://www.linkedin.com/in/milesprest0" target="_blank" rel="noreferrer">LinkedIn</a></BodyLine>
            <BodyLine>Google Visibility Lead: Justin Zelaya — improves search, maps, and reputation</BodyLine>
            <BodyLine><a href="mailto:justin@prest0.ai">justin@prest0.ai</a></BodyLine>
          </SectionCard>

          <SectionCard>
            <SectionTitle>Firm Context</SectionTitle>
            <BodyLine>Practice areas: Immigration law, workers' compensation, and employment law</BodyLine>
            <BodyLine>Clientele: Primarily Spanish-speaking — all AI tools support full bilingual workflows</BodyLine>

          </SectionCard>
        </SectionRow>

        <SectionRow>
          <SectionCard>
            <SectionTitle>Timeline</SectionTitle>
            <TimelineList>
              <TimelineItem>
                <TimelineLabel>Week 2 · Jan 5</TimelineLabel>
                <TimelineText>Advanced lawyer research tool for niche cases — instant case law briefs. (launched)</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Ongoing</TimelineLabel>
                <TimelineText>Search visibility tune-up — better Google and maps so clients find you first. (much progress)</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Week 5 · Jan 26</TimelineLabel>
                <TimelineText>Client Q&A chatbot — answers questions, upsells reports ($50) or calls ($100). (in progress)</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Week 6 · Feb 2</TimelineLabel>
                <TimelineText>Legal document creator — employment agreements, NDAs, contractor agreements, demand letters, and I-589 asylum applications. Full bilingual English/Spanish support. Upload intake documents to auto-populate forms. (launched)</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Week 7 · Feb 9</TimelineLabel>
                <TimelineText>Legal change briefings — clear summaries and next steps for the firm.</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Week 8 · Feb 16</TimelineLabel>
                <TimelineText>Courtroom practice simulator (optional) — rehearse hearings and arguments.</TimelineText>
              </TimelineItem>
              <TimelineItem>
                <TimelineLabel>Weeks 9-19</TimelineLabel>
                <TimelineText>Adaptive agent rollout — automates workflows and learns firm preferences.</TimelineText>
              </TimelineItem>
            </TimelineList>
            <SingleLineMeta title="Milestone: adaptive agent that safely learns, reasons, and automates firm workflows with guardrails, remembered policies, and human approval.">
              Milestone: adaptive agent that safely learns, reasons, and automates firm workflows with guardrails, remembered policies, and human approval.
            </SingleLineMeta>
          </SectionCard>
        </SectionRow>

        <SectionRow>
          <SectionCard>
            <SectionTitle>Reference Documents</SectionTitle>
            <BodyLine><a href="https://drive.google.com/file/d/1Ilyl-2hX9zMJ1KkNylV2GIof1qbaICaV/view?usp=sharing" target="_blank" rel="noreferrer">1. Executed Letter of Intent (PDF)</a></BodyLine>
            <BodyLine><a href="https://drive.google.com/file/d/1OKkid65KFbbpaHNp3LjoaLjHqaa4lfaw/view?usp=sharing" target="_blank" rel="noreferrer">2. AI Transformation Program Contract (PDF)</a></BodyLine>
            <BodyLine><a href="https://docs.google.com/document/d/1J8S-B9wdaEJE3y0aVoF4WaevFSo1SendqvC4jPuM0nc/edit?usp=sharing" target="_blank" rel="noreferrer">3. Technical Roadmap and Planning (Doc)</a></BodyLine>
            <BodyLine><a href="https://docs.google.com/spreadsheets/d/17W_QbwGHxADQfYENsyMT7o2xiOJzkr4xP8oTM64xEZw/edit?usp=sharing" target="_blank" rel="noreferrer">4. prest0 .ai Information Center (Sheet)</a></BodyLine>
            <BodyLine><a href="https://drive.google.com/file/d/1fGzTqomwtfNAS9q9e_cVa6u7C52QkSLt/view?usp=sharing" target="_blank" rel="noreferrer">5. Google and Maps Spanish SEO forecast (PDF)</a></BodyLine>
            <BodyLine><a href="https://drive.google.com/file/d/1tIyBpVrK3sFZFtJeGLkHpAeRuoNX-QUQ/view?usp=sharing" target="_blank" rel="noreferrer">6. Deep research demonstration (PDF)</a></BodyLine>
            <BodyLine><a href="https://drive.google.com/file/d/1RK6WMEZHtA-LmTJx83k-CYJfa8eQvJJg/view?usp=sharing" target="_blank" rel="noreferrer">7. Initial legal document creation output (PDF)</a></BodyLine>
          </SectionCard>
        </SectionRow>
        </MainContent>

        <GlobalFooter />

        <PromptBarSpacer />
      </DockedContentWrapper>

      {/* GlobalChatSystem now rendered at App level — see App.tsx / pageContextRegistry */}
    </PageContainer>
  );
};

export default JusticeIsRight;

